import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';

const groupLabel = (label, items) => `${label} (${items.length})`;

const matchesFilter = (filter, ...fields) => {
  const terms = filter.toLowerCase().trim().split(/\s+/).filter(Boolean);
  const text = fields.join(' ').replace(/-/g, ' ').toLowerCase();
  return terms.every((term) => text.includes(term.replace(/-/g, ' ')));
};

const UserManualAdminMenu = ({ onSelectDraft, onSelectDuplicate, onDraftsLoaded = () => {}, refreshToken }) => {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const [drafts, setDrafts] = useState([]);
  const [pendingRevisions, setPendingRevisions] = useState([]);
  const [loadError, setLoadError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [duplicatePairs, setDuplicatePairs] = useState([]);
  const [duplicatesError, setDuplicatesError] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios
      .get('/user_manual/drafts.json')
      .then((response) => {
        if (cancelled) return;
        const loadedDrafts = response.data.drafts || [];
        const loadedRevisions = response.data.pending_revisions || [];
        setDrafts(loadedDrafts);
        setPendingRevisions(loadedRevisions);
        onDraftsLoaded([
          ...loadedDrafts.map((draft) => ({ ...draft, state: 'draft' })),
          ...loadedRevisions.map((revision) => ({ ...revision, state: 'revision' })),
        ]);
        setLoadError(false);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setDrafts([]);
        setPendingRevisions([]);
        onDraftsLoaded([]);
        setLoadError(true);
        setLoading(false);
        toast.error("Couldn't load drafts. Try again shortly.");
      });
    return () => { cancelled = true; };
  }, [refreshToken]);

  useEffect(() => {
    let cancelled = false;
    axios
      .get('/user_manual/duplicates.json')
      .then((response) => {
        if (cancelled) return;
        setDuplicatePairs(response.data.pairs || []);
        setDuplicatesError(false);
      })
      .catch(() => {
        if (cancelled) return;
        setDuplicatePairs([]);
        setDuplicatesError(true);
      });
    return () => { cancelled = true; };
  }, [refreshToken]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const unpublishedCount = drafts.length + pendingRevisions.length;
  const pendingCount = unpublishedCount + duplicatePairs.length;

  const visibleDuplicatePairs = duplicatePairs.filter((pair) => matchesFilter(filter, ...pair.articles.map((article) => article.slug)));

  const renderGroup = (label, items, state) => {
    const visibleItems = items.filter((item) => matchesFilter(filter, item.slug, item.section, item.chapter));
    if (visibleItems.length === 0) return null;
    return (
      <div className="user-manual-admin-menu-group">
        <div className="user-manual-admin-menu-group-title">{groupLabel(label, visibleItems)}</div>
        {visibleItems.map((item) => (
          <button
            key={item.slug}
            type="button"
            className="user-manual-admin-menu-item"
            onClick={() => { setOpen(false); onSelectDraft(item.slug, state); }}
          >
            <span className="user-manual-admin-menu-item-title">{item.slug.replace(/-/g, ' ')}</span>
            <span className="user-manual-admin-menu-item-path">{item.section} › {item.chapter}</span>
          </button>
        ))}
      </div>
    );
  };

  return (
    <div className="user-manual-admin-menu" ref={containerRef}>
      <button
        type="button"
        className="user-manual-admin-menu-toggle"
        onClick={() => setOpen((prev) => !prev)}
      >
        Admin
        {pendingCount > 0 && (
          <span className="user-manual-admin-menu-badge">{pendingCount}</span>
        )}
      </button>
      {open && (
        <div className="user-manual-admin-menu-dropdown">
          {pendingCount > 0 && (
            <input
              type="search"
              className="user-manual-admin-menu-filter"
              placeholder="Filter drafts..."
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              autoFocus
            />
          )}
          {loading && (
            <div className="user-manual-admin-menu-empty">Loading drafts…</div>
          )}
          {!loading && loadError && (
            <div className="user-manual-admin-menu-empty">Couldn't load drafts. Try again shortly.</div>
          )}
          {!loading && !loadError && unpublishedCount === 0 && (
            <div className="user-manual-admin-menu-empty">No drafts or pending revisions.</div>
          )}
          {renderGroup('Drafts', drafts, 'draft')}
          {renderGroup('Pending revisions', pendingRevisions, 'revision')}
          {duplicatesError && (
            <div className="user-manual-admin-menu-empty">Couldn't load possible duplicates.</div>
          )}
          {visibleDuplicatePairs.length > 0 && (
            <div className="user-manual-admin-menu-group">
              <div className="user-manual-admin-menu-group-title">{groupLabel('Possible duplicates', visibleDuplicatePairs)}</div>
              {visibleDuplicatePairs.map((pair) => (
                <button
                  key={pair.articles.map((article) => article.slug).join('/')}
                  type="button"
                  className="user-manual-admin-menu-item"
                  onClick={() => { setOpen(false); onSelectDuplicate(pair); }}
                >
                  <span className="user-manual-admin-menu-item-title">
                    {pair.articles.map((article) => article.slug.replace(/-/g, ' ')).join(' ↔ ')}
                  </span>
                  <span className="user-manual-admin-menu-item-path">
                    {pair.likely ? 'Likely duplicate' : 'Check'}
                    {` · ${Math.round(pair.score * 100)}% similar`}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default UserManualAdminMenu;
